import React, { useContext } from "react";
import { RouteComponentProps } from "react-router";
import { useMutation, useQuery } from "@apollo/client";

import { authRoutes } from "v2/routes/routesGroups/authRoutes";

import { useFullPageLoader } from "v2/hooks/useFullPageLoader";

import { ApplicationAsset, AssetType, Status } from "api/models";
import {
  updateReferenceVariation as updateReferenceVariationMutation,
  UpdateReferenceVariationResponse,
} from "v2/api/mutations/application/updateReferenceVariation";
import { UPDATE_CONTROL_ICON } from "api/mutations/assets";

import { useHasUserPermission } from "v2/hooks/useHasUserPermission";

import {
  createIcons,
  deactivateIcon,
  DeactivateIconResponse,
  deleteIcon,
  DeleteIconResponse,
} from "v2/api/mutations/application/icons";
import { GET_APPLICATION_ICONS } from "api/queries/assets";

import { getTrafficIndicator } from "utils/statistics";

import { runAction } from "../helpers/runAction";
import { updateReferenceVariation } from "../helpers/updateReferenceVariation";

import { ApplicationContext, ModalContext } from "contexts";

import Page from "./ApplicationElementsIconsPage";
import { Spinner, Error } from "components";
import ApplicationElements from "screens/Application/ApplicationElements/components/ApplicationElementsContainer";

interface Props extends RouteComponentProps<{ id: string }> {}

// TODO: to refactor like text assets
function ApplicationElementsIconsContainer({ history, match }: Props) {
  const hasUserPermissions = useHasUserPermission();

  const applicationId = match.params.id;
  const { data, error, loading } = useQuery(GET_APPLICATION_ICONS, {
    variables: { applicationId },
    fetchPolicy: "network-only",
  });

  const [fullPageLoader, setFullPageLoader] = useFullPageLoader();

  const [deleteMutation] = useMutation<DeleteIconResponse>(
    deleteIcon.definition,
  );
  // TODO: check if we use it ask gabi or paweł
  const [controlMutation] = useMutation(UPDATE_CONTROL_ICON);
  const [updateReferenceMutation] = useMutation<
    UpdateReferenceVariationResponse
  >(updateReferenceVariationMutation.definition);
  const [deactivateMutation] = useMutation<DeactivateIconResponse>(
    deactivateIcon.definition,
  );

  const setModal = useContext(ModalContext);
  const applicationCtx = useContext(ApplicationContext);

  const refetchQuery = {
    query: GET_APPLICATION_ICONS,
    variables: { applicationId },
  };

  const trafficIndicator =
    applicationCtx.current &&
    data &&
    applicationCtx.current.status === Status.active
      ? getTrafficIndicator(data.phaseTraffic, data.trafficNeeded)
      : undefined;

  const removeAsset = React.useCallback(
    (asset: ApplicationAsset) => async () => {
      await runAction(
        applicationId,
        asset,
        deleteMutation,
        setModal,
        refetchQuery,
        setFullPageLoader,
      );
    },
    [applicationId, deleteMutation, setModal, refetchQuery, setFullPageLoader],
  );

  const displayRemoveModal = React.useMemo(() => {
    if (!hasUserPermissions(deleteIcon.requiredPermissions)) {
      return null;
    }

    return (asset: ApplicationAsset) => {
      setModal({
        type: "set",
        props: {
          title: "Delete",
          content: "Are you sure you want to delete this asset?",
          onConfirm: removeAsset(asset),
        },
      });
    };
  }, [hasUserPermissions, setModal, removeAsset]);

  const onRemoveAssetFromCurrentTest = React.useCallback(
    (asset: ApplicationAsset) => async () => {
      await runAction(
        applicationId,
        asset,
        deactivateMutation,
        setModal,
        refetchQuery,
        setFullPageLoader,
      );
    },
    [
      applicationId,
      deactivateMutation,
      setModal,
      refetchQuery,
      setFullPageLoader,
    ],
  );

  const removeAssetFromCurrentTestModal = React.useMemo(() => {
    if (!hasUserPermissions(deactivateIcon.requiredPermissions)) {
      return null;
    }

    return (asset: ApplicationAsset) =>
      setModal({
        type: "set",
        props: {
          title: "Remove Asset From Test",
          content:
            "Are you sure you want to remove this asset from current test?",
          onCancel: () => {},
          onConfirm: onRemoveAssetFromCurrentTest(asset),
        },
      });
  }, [hasUserPermissions, setModal, onRemoveAssetFromCurrentTest]);

  const onControl = async (asset: ApplicationAsset) => {
    await runAction(
      applicationId,
      asset,
      controlMutation,
      setModal,
      refetchQuery,
      setFullPageLoader,
    );
  };

  const onSetTop = React.useMemo(() => {
    if (
      !hasUserPermissions(updateReferenceVariationMutation.requiredPermissions)
    ) {
      return null;
    }

    return async (asset: ApplicationAsset) => {
      await updateReferenceVariation(
        applicationId,
        asset,
        AssetType.icon,
        updateReferenceMutation,
        setModal,
        refetchQuery,
        setFullPageLoader,
      );
    };
  }, [
    hasUserPermissions,
    refetchQuery,
    setFullPageLoader,
    setModal,
    updateReferenceMutation,
    applicationId,
  ]);

  if (loading || !data) {
    return <Spinner />;
  }

  if (error) {
    return <Error errorElement={error.message} />;
  }

  return (
    <ApplicationElements
      applicationId={applicationId}
      history={history}
      uploadRoute={authRoutes.APPLICATION_CREATE_ICONS.getUrlWithParams({
        id: applicationId,
      })}
      permissionsForUpload={createIcons.requiredPermissions}
    >
      {fullPageLoader}
      <Page
        applicationId={applicationId}
        currentPhase={data.currentPhase}
        assets={data.icons}
        onRemove={displayRemoveModal}
        onDeactivate={removeAssetFromCurrentTestModal}
        onSetTop={onSetTop}
        onControl={onControl}
        history={history}
        trafficIndicator={trafficIndicator}
        currentIteration={data.currentIteration}
      />
    </ApplicationElements>
  );
}

export default ApplicationElementsIconsContainer;
